import * as React from 'react';
import { useCallback, useEffect } from 'react';
import isEqual from 'react-fast-compare';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    openCompleteAccountSignupModalFromChecker,
    stopTracker,
} from '@/redux/modules/account/shouldLogin/shouldLogin.actions';
import {
    getTriggerId,
    isCompleteAccountModalOpen,
    isTracking,
} from '@/redux/modules/account/shouldLogin/shouldLogin.selectors';
import { getUiUpdateAccount } from '@/redux/modules/account/updateAccount/updateAccount';
import {
    getDefaultAddressIsVerified,
    isProfileComplete,
    isUserAtLeastPartiallyRegistered,
    isUserLoggedIn,
} from '@/redux/modules/account/user/user.selectors';
import { ModalContext } from '@/redux/modules/modal';
import { AddressModalData } from '@/types/address/address.types';
import { SearchParams as SearchParamsType } from '@/types/SaveSearch';

export type ShouldLogInContainerProps<RunFunctionArgumentType = void> = {
    allSearchParams?: SearchParamsType;
    children: React.ReactNode;
    completeAddressData?: AddressModalData;
    forceConfirmPassword?: boolean;
    forceSignUp?: boolean;
    modalContext?: ModalContext;
    requireCompleteProfile?: boolean;
    requireLogin?: boolean;
    requireUserAtLeastPartiallyRegistered?: boolean;
    requireVerifiedAddress?: boolean;
    runFunction?: (arg: RunFunctionArgumentType) => void;
    runFunctionArgument?: RunFunctionArgumentType | undefined;
};

// prettier-ignore
const ShouldLogInContainer = <RunFunctionArgumentType,>({
    children,
    forceSignUp = true,
    modalContext,
    requireCompleteProfile = false,
    requireLogin = true,
    requireUserAtLeastPartiallyRegistered,
    requireVerifiedAddress = false,
    runFunction,
    runFunctionArgument,
}: ShouldLogInContainerProps<RunFunctionArgumentType>) => {
    const guid = React.useMemo(() => uuidv4(), []);
    const dispatch = useAppDispatch();

    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const isCompleteProfile = useAppSelector(isProfileComplete);
    const triggerId = useAppSelector(getTriggerId, isEqual);
    const tracking = useAppSelector(isTracking, isEqual);
    const completeAccountModalOpen = useAppSelector(isCompleteAccountModalOpen);
    const userAtLeastPartiallyRegistered = useAppSelector(isUserAtLeastPartiallyRegistered);
    const uiUpdateAccount = useAppSelector(getUiUpdateAccount);

    const isDefaultAddressVerified = useAppSelector(getDefaultAddressIsVerified);

    // ensure that the same function isnt run twice in quick succession
    // Trailing edge: a click inside something that unmounts on click (a menu) drops this call, so wire those
    // through modalContext.runAtLoginFunction instead.
    const callback = useDebouncedCallback(runFunction || (() => {}), 50);
    // Leading edge: modalContext is how menu items run actions, and a menu unmounts this container on
    // click, which would drop a trailing call before it fires.
    const contextCallback = useDebouncedCallback(modalContext?.runAtLoginFunction || (() => {}), 50, {
        leading: true,
        trailing: false,
    });

    // Determines page-level container behavior on change, either running passed in function,
    // or running component logic if we're not confirming password
    useEffect(() => {
        if (guid === triggerId && tracking) {
            if (
                !completeAccountModalOpen &&
                userAtLeastPartiallyRegistered &&
                (userLoggedIn || !requireLogin) &&
                (isCompleteProfile || !requireCompleteProfile) &&
                Boolean(runFunction)
            ) {
                dispatch(stopTracker());
                callback(runFunctionArgument);
            }

            if (
                userLoggedIn &&
                requireCompleteProfile &&
                !isCompleteProfile &&
                !completeAccountModalOpen &&
                !uiUpdateAccount.submitted &&
                !uiUpdateAccount.success
            ) {
                dispatch(openCompleteAccountSignupModalFromChecker({ modalContext, trigger: guid }));
            }
        }
    }, [
        callback,
        completeAccountModalOpen,
        dispatch,
        guid,
        isCompleteProfile,
        requireCompleteProfile,
        requireLogin,
        modalContext,
        runFunction,
        runFunctionArgument,
        tracking,
        triggerId,
        uiUpdateAccount.submitted,
        uiUpdateAccount.success,
        userAtLeastPartiallyRegistered,
        userLoggedIn,
    ]);

    const handleClick = useCallback(
        async (event: MouseEvent) => {
            event?.stopPropagation?.();
            if (
                (forceSignUp && !userLoggedIn && !userAtLeastPartiallyRegistered) ||
                (forceSignUp && !userLoggedIn && requireLogin) ||
                (!userAtLeastPartiallyRegistered && requireUserAtLeastPartiallyRegistered) ||
                (!isCompleteProfile && requireCompleteProfile) ||
                (isCompleteProfile && requireVerifiedAddress && !isDefaultAddressVerified)
            ) {
                // populate modalContext with runAtLoginFunction if using ShouldLoginContainer
                // from pop-up menu or any disappearing element to persist action
                dispatch(openCompleteAccountSignupModalFromChecker({ modalContext, trigger: guid }));
            } else if (runFunction) {
                callback(runFunctionArgument);
            } else if (modalContext.runAtLoginFunction) {
                contextCallback(modalContext.runAtLoginArgs);
            }
        },
        [
            callback,
            contextCallback,
            dispatch,
            forceSignUp,
            guid,
            isCompleteProfile,
            isDefaultAddressVerified,
            modalContext,
            requireCompleteProfile,
            requireLogin,
            requireUserAtLeastPartiallyRegistered,
            requireVerifiedAddress,
            runFunction,
            runFunctionArgument,
            userAtLeastPartiallyRegistered,
            userLoggedIn,
        ]
    );

    // @ts-expect-error
    return children ? React.cloneElement(React.Children.only(children), { onClick: handleClick }) : null;
};

export default ShouldLogInContainer;
